import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { calcularCronograma, calcularCurvaAbc, type ClasseAbc } from "../../../shared/planejamento";
import { baixarArquivo } from "../api/cliente";
import { useOrcamento } from "../api/consultas";
import { useAvisos } from "../components/Avisos";
import { Carregando, ErroCarregamento, formatarMoeda, formatarQuantidade, lerNumero, Tabela } from "../components/ui";
import { useIdOrcamento } from "./OrcamentoDetalhe";

const pct = (v: number) => `${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/** Curva acumulada (0–100%) em SVG; `marcas` = linhas horizontais de referência. */
function CurvaAcumulada({ pontos, rotulos, marcas = [] }: { pontos: number[]; rotulos?: string[]; marcas?: number[] }) {
  const L = 640;
  const A = 220;
  const m = { e: 40, d: 10, t: 10, b: 24 };
  const x = (i: number) => m.e + (pontos.length <= 1 ? 0 : (i / (pontos.length - 1)) * (L - m.e - m.d));
  const y = (v: number) => m.t + (1 - v / 100) * (A - m.t - m.b);
  const caminho = [`M ${x(0)} ${y(0)}`, ...pontos.map((v, i) => `L ${x(i)} ${y(v)}`)].join(" ");
  // Começa no zero: a curva parte da origem antes do 1º ponto.
  const xs = (i: number) => m.e + ((i + 1) / pontos.length) * (L - m.e - m.d);
  const caminhoS = [`M ${m.e} ${y(0)}`, ...pontos.map((v, i) => `L ${xs(i)} ${y(v)}`)].join(" ");
  const usarS = Boolean(rotulos);
  return (
    <svg viewBox={`0 0 ${L} ${A}`} style={{ width: "100%", maxWidth: L, display: "block" }} role="img" aria-label="Curva acumulada">
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}>
          <line x1={m.e} x2={L - m.d} y1={y(v)} y2={y(v)} stroke="currentColor" strokeOpacity={0.12} />
          <text x={m.e - 6} y={y(v) + 4} fontSize={10} textAnchor="end" fill="currentColor" opacity={0.6}>
            {v}%
          </text>
        </g>
      ))}
      {marcas.map((v) => (
        <line key={`m${v}`} x1={m.e} x2={L - m.d} y1={y(v)} y2={y(v)} stroke="#F9891A" strokeDasharray="4 4" />
      ))}
      <path d={usarS ? caminhoS : caminho} fill="none" stroke="#2f7fd1" strokeWidth={2.5} />
      {rotulos?.map((r, i) => (
        <text key={r} x={xs(i)} y={A - 6} fontSize={10} textAnchor="middle" fill="currentColor" opacity={0.6}>
          {r}
        </text>
      ))}
    </svg>
  );
}

function Cabecalho({ titulo, id, nome }: { titulo: string; id: number; nome: string }) {
  return (
    <>
      <p className="note no-print">
        <Link to={`/orcamentos/${id}`}>← Voltar ao orçamento</Link>
      </p>
      <h1>
        {titulo}: {nome}
      </h1>
    </>
  );
}

function BotaoExcel({ url, arquivo }: { url: string; arquivo: string }) {
  const { avisarErro } = useAvisos();
  const [baixando, setBaixando] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-ghost"
      disabled={baixando}
      onClick={async () => {
        setBaixando(true);
        try {
          await baixarArquivo(url, arquivo);
        } catch (erro) {
          avisarErro(erro);
        } finally {
          setBaixando(false);
        }
      }}
    >
      {baixando ? "Gerando..." : "Exportar Excel"}
    </button>
  );
}

const CORES: Record<ClasseAbc, string> = { A: "badge-warn", B: "badge-blue", C: "badge-muted" };

export function OrcamentoCurvaAbc() {
  const id = useIdOrcamento();
  const { data, isLoading, error } = useOrcamento(id);
  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;
  const abc = calcularCurvaAbc(data.resumo);

  return (
    <>
      <Cabecalho titulo="Curva ABC" id={id} nome={data.orcamento.nome} />
      <div className="no-print" style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn btn-ghost" onClick={() => window.print()}>
          Imprimir / PDF
        </button>
        <BotaoExcel url={`/api/orcamentos/${id}/curva-abc.xlsx`} arquivo={`curva_abc_${id}.xlsx`} />
      </div>
      <div className="card">
        <Tabela>
          <thead>
            <tr>
              <th>Classe</th>
              <th className="num">Itens</th>
              <th className="num">Valor (R$)</th>
              <th className="num">% do valor</th>
            </tr>
          </thead>
          <tbody>
            {(["A", "B", "C"] as ClasseAbc[]).map((c) => (
              <tr key={c}>
                <td>
                  <span className={`badge ${CORES[c]}`} style={{ marginLeft: 0 }}>{c}</span>
                </td>
                <td className="num">{abc.resumo[c].itens}</td>
                <td className="num">{formatarMoeda(abc.resumo[c].total)}</td>
                <td className="num">{pct(abc.resumo[c].percentual)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 700 }}>Total</td>
              <td className="num">{abc.linhas.length}</td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatarMoeda(abc.total)}
              </td>
              <td className="num">100,00%</td>
            </tr>
          </tbody>
        </Tabela>
        <CurvaAcumulada pontos={abc.linhas.map((l) => l.acumulado)} marcas={[abc.limites.a, abc.limites.b]} />
        <p className="note">
          Classe A: itens que somam os primeiros {abc.limites.a}% do valor; B: até {abc.limites.b}%; C: o restante. Valores com
          BDI (serviços e canteiro com BDI de serviços; seção de materiais com BDI de materiais). O mesmo código com o mesmo
          preço em seções diferentes aparece somado.
        </p>
      </div>

      <div className="card">
        <Tabela>
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Código</th>
              <th>Descrição</th>
              <th>Un.</th>
              <th className="num">Qtd.</th>
              <th className="num">Preço Unit. c/ BDI</th>
              <th className="num">Total c/ BDI</th>
              <th className="num">%</th>
              <th className="num">% acum.</th>
              <th>Classe</th>
            </tr>
          </thead>
          <tbody>
            {abc.linhas.length === 0 && (
              <tr>
                <td colSpan={10} className="note">
                  Orçamento sem itens.
                </td>
              </tr>
            )}
            {abc.linhas.map((l) => (
              <tr key={`${l.codigo}|${l.posicao}`}>
                <td className="num">{l.posicao}</td>
                <td>{l.codigo}</td>
                <td>{l.descricao}</td>
                <td>{l.unidade}</td>
                <td className="num">{formatarQuantidade(l.quantidade)}</td>
                <td className="num">{formatarMoeda(l.preco_unitario)}</td>
                <td className="num">{formatarMoeda(l.total)}</td>
                <td className="num">{pct(l.percentual)}</td>
                <td className="num">{pct(l.acumulado)}</td>
                <td>
                  <span className={`badge ${CORES[l.classe]}`} style={{ marginLeft: 0 }}>{l.classe}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </div>
    </>
  );
}

export function OrcamentoCronograma() {
  const id = useIdOrcamento();
  const { data, isLoading, error } = useOrcamento(id);
  const [params, setParams] = useSearchParams();
  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;

  // Prazo: o informado na tela; senão o multiplicador da administração local (meses); senão 6.
  const prazoCanteiro = data.canteiro?.administracao.multiplicador_padrao ?? 0;
  const prazo = Math.min(120, Math.max(1, Math.round(lerNumero(params.get("prazo") ?? "", prazoCanteiro > 0 ? prazoCanteiro : 6))));
  const cr = calcularCronograma(data.resumo, prazo);
  const meses = Array.from({ length: prazo }, (_, i) => `Mês ${i + 1}`);

  return (
    <>
      <Cabecalho titulo="Cronograma físico-financeiro" id={id} nome={data.orcamento.nome} />
      <div className="form-row no-print">
        <label>
          Prazo da obra (meses)
          <input
            type="number"
            min={1}
            max={120}
            value={prazo}
            style={{ width: 90 }}
            onChange={(e) => setParams(e.target.value ? { prazo: e.target.value } : {}, { replace: true })}
          />
        </label>
        <button type="button" className="btn btn-ghost" onClick={() => window.print()}>
          Imprimir / PDF
        </button>
        <BotaoExcel url={`/api/orcamentos/${id}/cronograma.xlsx?prazo=${prazo}`} arquivo={`cronograma_${id}.xlsx`} />
      </div>
      <p className="only-print">Prazo: {prazo} meses</p>

      <div className="card" style={{ overflowX: "auto" }}>
        <Tabela>
          <thead>
            <tr>
              <th>Etapa</th>
              <th className="num">Total (R$)</th>
              {meses.map((m) => (
                <th key={m} className="num">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cr.linhas.map((l) => (
              <tr key={`${l.tipo}${l.numero}${l.descricao}`}>
                <td>
                  {l.numero && <strong>{l.numero} </strong>}
                  {l.descricao}
                </td>
                <td className="num">{formatarMoeda(l.total)}</td>
                {l.meses.map((v, i) => (
                  <td key={i} className="num">
                    {v > 0.005 ? (
                      <>
                        {formatarMoeda(v)}
                        <br />
                        <span className="note">{pct(l.total > 0 ? (v / l.total) * 100 : 0)}</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="linha-secao">
              <td>Desembolso mensal</td>
              <td className="num">{formatarMoeda(cr.total)}</td>
              {cr.mensal.map((v, i) => (
                <td key={i} className="num">
                  {formatarMoeda(v)}
                  <br />
                  <span className="note">{pct(cr.percentual_mensal[i])}</span>
                </td>
              ))}
            </tr>
            <tr className="linha-secao">
              <td>Acumulado</td>
              <td />
              {cr.acumulado.map((v, i) => (
                <td key={i} className="num">
                  {formatarMoeda(v)}
                  <br />
                  <span className="note">{pct(cr.percentual_acumulado[i])}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </Tabela>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Curva S (desembolso acumulado)</h2>
        <CurvaAcumulada pontos={cr.percentual_acumulado} rotulos={meses.map((_, i) => String(i + 1))} />
        <p className="note">
          Critérios de desembolso (orientação do TCU, Acórdão nº 2.622/2013-Plenário): os serviços de cada etapa seguem uma
          distribuição normal (gaussiana) centrada no meio do prazo, que acumulada forma a curva S; a mobilização é paga
          integralmente na 1ª medição; a desmobilização, na última; e a administração local é paga proporcionalmente à
          execução dos serviços em cada mês, e não em parcelas fixas. Valores com BDI. Conferir exigências específicas do
          TCE-BA e do edital.
        </p>
      </div>
    </>
  );
}
